import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Text,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Heading,
  VStack,
  Spinner,
  SimpleGrid,
  Divider,
  Button,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { constants } from "constants/constants";

const API_URL = constants.apiUrl;

export default function ViewAmazonOrder() {
  const getQueryParams = (url) => {
    const queryString = url.split("?")[1];
    if (!queryString) return {};
    const paramsArray = queryString.split("&");
    const params = {};
    paramsArray.forEach((param) => {
      const [key, value] = param.split("=");
      params[key] = decodeURIComponent(value);
    });
    return params;
  };

  const queryParams = getQueryParams(window.location.href);
  const JiwaOrderNo = queryParams["JiwaOrderNo"] || "";
  const AmazonInvoiceNo = queryParams["AmazonInvoiceNo"] || "";

  const [orderData, setOrderData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editableLines, setEditableLines] = useState({});
  const [modalData, setModalData] = useState([]);
  const [selectedBarcode, setSelectedBarcode] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const response = await axios.get(`${API_URL}/get-amazon-orders`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            JiwaOrderNo,
            AmazonInvoiceNo,
            Days: 7,
          },
          withCredentials: true,
        });

        const records = response.data.records;
        const order = Array.isArray(records) ? records[0] : records;
        setOrderData(order || null);
      } catch (err) {
        console.error("Failed to fetch order data:", err);
        setError("Failed to load order details.");
      } finally {
        setIsLoading(false);
      }
    };

    if (JiwaOrderNo || AmazonInvoiceNo) {
      fetchOrderData();
    }
  }, [JiwaOrderNo, AmazonInvoiceNo]);

  const filteredOrderLines = Array.isArray(orderData?.OrderLines)
    ? orderData.OrderLines.filter((line) =>
      line.PartNo?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : [];

  const renderDetail = (label, value) => (
    <Text fontSize="md">
      <Text as="span" fontWeight="bold" mr={2}>
        {label}:
      </Text>
      <Text as="span">{value}</Text>
    </Text>
  );

  // 🔹 Handle editable DkaQuantity changes

  const handleQtyChange = (index, value, maxQty, sohQty) => {
    // Allow clearing the field (shows empty, treated as 0)
    if (value === "") {
      setEditableLines((prev) => ({ ...prev, [index]: "" }));
      return;
    }

    const numValue = Number(value);
    const maxAllowed = Math.min(maxQty || 0, sohQty || 0);

    // Ensure numeric and within allowed limits
    if (isNaN(numValue) || numValue < 0 || numValue > maxAllowed) return;

    setEditableLines((prev) => ({ ...prev, [index]: numValue }));
  };


  // 🔹 Handle Update Quantities API call
// 🔹 Handle Update Quantities API call
const handleUpdateQuantities = async () => {
  try {
    const token = localStorage.getItem("accessToken");

    // Construct payload with PartNo + updated Qty
    const Records = Object.entries(editableLines).map(([index, qty]) => {
      const line = orderData.OrderLines[index];
      return {
        PartNo: line.PartNo,
        AmzQuantity: line.AmzQuantity,
        DkaQuantity: qty,
        DkaPrice: line.DkaPrice,
        AmzPrice: line.AmzPrice
      };
    });

    const payload = {
      AmazonOrderNo: orderData?.AmazonOrderNo,
      JiwaOrderNo: orderData?.JiwaOrderNo,
      Records,
    };

    await axios.post(`${API_URL}/update-davka-qty`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    alert("Quantities updated successfully!");
    setEditableLines({});
  } catch (error) {
    console.error("Failed to update quantities:", error);
    alert("Failed to update quantities.");
  }
};



  // 🔹 Open modal for missing PartNo and fetch suggestions
  const handleFindPart = async (barcode) => {
    setSelectedBarcode(barcode);
    onOpen();
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.get(`${API_URL}/find-part-by-barcode`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { barcode },
      });
      setModalData(response.data.results || []);
    } catch (err) {
      console.error("Failed to fetch parts:", err);
      setModalData([]);
    }
  };

  return (
    <Flex direction="column" align="center" justify="center" minHeight="100vh"  bg="gray.50">
      <Box bg="white" p={{ base: "4", md: "8" }} borderRadius="lg" boxShadow="xl" w="100%" maxW="1250px">
        <Heading size="xl" mb={6} textAlign="center" color="gray.700">
          Order Details
        </Heading>
        <Divider mb={6} />

        {isLoading ? (
          <Flex height="200px" justify="center" align="center">
            <Spinner size="xl" thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" />
          </Flex>
        ) : error ? (
          <Text color="red.500" textAlign="center">{error}</Text>
        ) : !orderData ? (
          <Text color="gray.500" textAlign="center">No order data found.</Text>
        ) : (
          <VStack spacing={8} align="stretch">
            {/* Order Summary Section */}
            <Box bg="blue.50" p={6} borderRadius="lg">
              <Heading size="md" mb={4} color="blue.700">Order Summary</Heading>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {renderDetail("Amazon Order No", orderData.AmazonOrderNo)}
                {renderDetail("Jiwa Order No", orderData.JiwaOrderNo)}
                {renderDetail("Amazon Order Type", orderData.AmazonOrderType)}
                {renderDetail("Order Date", orderData.OrderDate)}
                {renderDetail("Status", orderData.OrderStatus)}
                {renderDetail("Amazon Total", `$${Number(orderData.AmazonOrderTotal || 0).toFixed(2)}`)}
                {renderDetail("Davka Total", `$${Number(orderData.DavkaOrderTotal || 0).toFixed(2)}`)}
              </SimpleGrid>
            </Box>

            <Divider />

            {/* Order Lines Section */}
            <Box w="100%" border="1px" borderColor="gray.200" borderRadius="lg">
              <Table variant="simple" size="sm" width="100%" style={{ tableLayout: "fixed" }}>
                <Thead bg="gray.100">
                  <Tr>
                    <Th width="15%">Part No</Th>
                    <Th width="25%">Product Name</Th>
                    <Th width="15%">ASIN</Th>
                    <Th width="15%">Barcode</Th>
                    <Th isNumeric width="10%">Amz Price</Th>
                    <Th isNumeric width="10%">Dka Price</Th>
                    <Th isNumeric width="7%">Amz Qty</Th>
                    <Th isNumeric width="12%">Dka Qty</Th>
                    <Th isNumeric width="8%">SOH</Th>
                  </Tr>
                </Thead>

                <Tbody>
                  {filteredOrderLines.length > 0 ? (
                    filteredOrderLines.map((line, index) => {
                      let rowBg = "transparent";
                      if (!line.PartNo || line.PartNo.trim() === "") rowBg = "blue.200";
                      else if (Number(Number(line.AmzPrice).toFixed(2)) < Number(Number(line.DkaPrice).toFixed(2)))
                        rowBg = "yellow.200";
                      else if (Number(line.DkaQuantity) === 0) rowBg = "red.300";



                      return (
                        <Tr key={index} bg={rowBg}>
                          <Td fontSize="sm">
                            {line.PartNo ? (
                              line.PartNo
                            ) : (
                              <IconButton
                                icon={<SearchIcon />}
                                size="sm"
                                colorScheme="blue"
                                aria-label="Find Part"
                                onClick={() => handleFindPart(line.Barcode)}
                              />
                            )}
                          </Td>
                          <Td fontSize="sm">{line.ProductName}</Td>
                          <Td fontSize="sm">{line.ASIN}</Td>
                          <Td fontSize="sm">{line.Barcode || "-"}</Td>
                          <Td isNumeric fontSize="sm">${Number(line.AmzPrice || 0).toFixed(2)}</Td>
                          <Td isNumeric fontSize="sm">${Number(line.DkaPrice || 0).toFixed(2)}</Td>
                          <Td isNumeric fontSize="sm">{line.AmzQuantity || 0}</Td>

                          <Td isNumeric fontSize="sm">
                            {orderData.OrderStatus !== "Closed" ? (
                              <Input
                                size="sm"
                                type="number"
                                min={0}
                                max={Math.min(line.AmzQuantity || 0, line.SOH || 0)}
                                value={
                                  editableLines[index] !== undefined
                                    ? editableLines[index]
                                    : line.DkaQuantity ?? 0
                                }
                                onChange={(e) =>
                                  handleQtyChange(index, e.target.value, line.AmzQuantity, line.SOH)
                                }
                                border="1px solid #aeaeae"
                                outline="2px solid #aeaeae"
                                _focus={{
                                  outline: "2px solid #aeaeae",
                                  borderColor: "#aeaeae",
                                  boxShadow: "none",
                                }}
                                _hover={{
                                  borderColor: "#aeaeae",
                                }}
                                isDisabled={!line.SOH || Number(line.SOH) <= 0} // 🔹 Disable when SOH = 0 or null
                                bg={(!line.SOH || Number(line.SOH) <= 0) ? "gray.100" : "white"} // 🔹 Gray background for disabled
                                cursor={(!line.SOH || Number(line.SOH) <= 0) ? "not-allowed" : "auto"}
                              />
                            ) : (
                              line.DkaQuantity || 0
                            )}
                          </Td>

                          <Td isNumeric fontSize="sm">{line.SOH || 0}</Td>
                        </Tr>
                      );
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={8} textAlign="center" py={4} color="gray.500">
                        No matching items found.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>

            {/* Update button (only if editable) */}
            {orderData.OrderStatus !== "Closed" && (
              <Button
                colorScheme="blue"
                alignSelf="flex-end"
                onClick={handleUpdateQuantities}
              >
{!orderData?.JiwaOrderNo ? "Add to Jiwa" : "Update to Jiwa"}
              </Button>
            )}

          </VStack>
        )}
      </Box>

      {/* 🔹 Modal for missing PartNo */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Available Parts for Barcode: {selectedBarcode}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {modalData.length > 0 ? (
              modalData.map((item, i) => (
                <Box key={i} p={3} borderWidth="1px" borderRadius="md" mb={2}>
                  <Text><b>Part No:</b> {item.PartNo}</Text>
                  <Text><b>Name:</b> {item.Description}</Text>
                  <Text><b>Barcode:</b> {item.Barcode}</Text>
                  <Text><b>SOH:</b> {item.SOH || 0}</Text>
                  {console.log(item.SOH)}
                  {(!item.SOH && item.SOH === undefined && item.SOH !== null) || item.SOH === 0 || item.SOH === "" ? (
                    <Button
                      colorScheme="red"
                      mt={2}
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem("accessToken");
                          await axios.post(
                            `${API_URL}/action-remove-barcode`,
                            { PartNo: item.PartNo, Action: "RemoveBarcode" },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          alert(`Barcode for ${item.PartNo} removed successfully`);
                          onClose();
                        } catch (err) {
                          console.error("Failed to remove barcode:", err);
                          alert("Failed to remove barcode");
                        }
                      }}
                    >
                      Remove Barcode
                    </Button>
                  ) : null}
                </Box>
              ))
            ) : (
              <Text color="gray.500">No parts found for this barcode.</Text>
            )}

          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
